import { safeParseJson } from '../lib/json.js'
import { logger } from '../lib/logger.js'
import { issueLiveSessionCapability } from '../core/session/session-identity.js'
import { WEBSOCKET_CONFIG } from '../config/constants.js'
import {
  LIVE_LINE_NOT_STORED,
  LIVE_LINE_UNCONFIRMED,
  LIVE_TYPED_LINE_CLIENT_RESENDS,
  LIVE_USER_ASR_ENDPOINT_PAUSE_MS,
} from '../core/contracts.js'
import { createGatewayConnection, type GatewayConnection } from './liveAuth.js'
import { normalizeLiveToolCalls, normalizeLiveToolResponsePayload } from './liveTools.js'
import type {
  LiveConnectArgs,
  LiveContextUpdate,
  LiveGatewayClientMessage,
  LiveGatewayRealtimeInputPayload,
  LiveGatewayServerMessage,
  LiveGatewaySessionStartPayload,
  LiveMediaFrame,
  LivePolicyUpdate,
  LiveSessionAdapter as LiveSessionAdapterContract,
  LiveSessionEvent,
  LiveSessionState,
} from './liveTypes.js'

type EventListener = (event: LiveSessionEvent) => void

type PendingConnect = {
  resolve: () => void
  reject: (error: Error) => void
}

/** Gemini Live invalid-frame close code. A 1007 means the session is poisoned and must not be reused. */
const LIVE_INVALID_FRAME_CLOSE_CODE = 1007

function createLiveStartAttemptId(): string {
  const cryptoRef = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto
  if (cryptoRef?.randomUUID) return `live-${cryptoRef.randomUUID()}`
  return `live-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

function createLiveInputEventId(): string {
  const cryptoRef = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto
  if (cryptoRef?.randomUUID) return `input-${cryptoRef.randomUUID()}`
  return `input-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

export class LiveSessionAdapter implements LiveSessionAdapterContract {
  private connection: GatewayConnection | null = null
  private state: LiveSessionState = 'disconnected'
  private readonly listeners = new Set<EventListener>()
  private readonly cleanupCallbacks = new Set<() => void>()
  private pendingConnect: PendingConnect | null = null
  private lastConnectArgs: LiveConnectArgs | null = null
  private resumptionHandle: string | null = null
  private connectionId: string | null = null
  private isDisconnecting = false
  private liveStartAttemptId: string | null = null
  private hasConnectedBefore = false
  private previousCloseCode: number | null = null
  private previousCloseReason: string | null = null
  private activeAudioInputEventId: string | null = null
  /** Drop mic PCM briefly after a typed send so VAD can close and the model can answer. */
  private holdMicAudioUntilMs = 0
  /** Last typed realtime payload. Resent with the same input id if the server could not store it. */
  private typedLinePayload: LiveGatewayRealtimeInputPayload | null = null
  private typedLineResendsLeft = 0

  async connect(args: LiveConnectArgs): Promise<void> {
    logger.debug('[LiveSessionAdapter] connect() called', { currentState: this.state })
    if (this.state === 'connected' || this.state === 'connecting' || this.state === 'reconnecting') {
      logger.debug('[LiveSessionAdapter] connect() skipped — already in state', { state: this.state })
      return
    }

    // Always a fresh lifecycle: disconnect() resets the resumption handle so a
    // manual mic restart after a 1000/1007 close cannot reuse poisoned state.
    await this.disconnect()
    this.lastConnectArgs = args
    this.liveStartAttemptId = createLiveStartAttemptId()
    this.setState('connecting')

    logger.info('[LiveSessionAdapter] Live start (fresh connect)', {
      liveStartAttemptId: this.liveStartAttemptId,
      sessionId: args.sessionId,
      isResume: false,
      isReconnect: this.hasConnectedBefore,
      previousCloseCode: this.previousCloseCode,
      previousCloseReason: this.previousCloseReason,
    })

    try {
      logger.debug('[LiveSessionAdapter] creating gateway connection')
      const connection = await createGatewayConnection()
      logger.debug('[LiveSessionAdapter] gateway connection created', { url: connection.url })
      this.connection = connection
      this.bindConnection(connection)

      const sessionCapability = await issueLiveSessionCapability(args.sessionId ?? '')
      await this.startSession('session_start', {
        ...this.buildSessionStartPayload(args),
        sessionCapability,
      })
    } catch (error) {
      logger.debug('[LiveSessionAdapter] connect() error', { error: error instanceof Error ? error.message : String(error) })
      this.setState('error')
      this.emit({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to connect Live session',
      })
      await this.disconnect()
      throw error
    }
  }

  disconnect(): Promise<void> {
    if (this.isDisconnecting) return Promise.resolve()
    this.isDisconnecting = true

    const connection = this.connection
    const keepErrorState = this.state === 'error'
    // Only reject pending connect if we actually have one (avoid false error messages)
    if (this.pendingConnect) {
      this.rejectPendingConnect(new Error('Live session disconnected before start completed'))
    }

    this.cleanupConnection()
    this.connection = null
    this.connectionId = null
    // Reset transient per-connection state so the next connect() is a clean lifecycle.
    this.resumptionHandle = null

    if (connection?.isOpen()) {
      connection.send({ type: 'session_close', payload: { reason: 'client_disconnect' } })
      connection.close(1000, 'client_disconnect')
    } else {
      connection?.close()
    }

    this.isDisconnecting = false

    if (!keepErrorState && this.state !== 'disconnected') {
      this.setState('disconnected')
      this.emit({ type: 'disconnected', reason: 'client_disconnect' })
    }
    return Promise.resolve()
  }

  getState(): LiveSessionState {
    return this.state
  }

  sendRealtimeText(text: string): void {
    const trimmed = text.trim()
    if (!trimmed) return

    if (this.activeAudioInputEventId) {
      this.holdMicAudioUntilMs = Date.now() + LIVE_USER_ASR_ENDPOINT_PAUSE_MS
    }

    this.sendRealtimeInput({
      inputEventId: createLiveInputEventId(),
      text: trimmed,
      turnComplete: true,
    })
  }

  sendAudioChunk(chunk: { mimeType: string; data: string }): void {
    if (Date.now() < this.holdMicAudioUntilMs) return
    this.activeAudioInputEventId ??= createLiveInputEventId()
    this.sendRealtimeInput({
      inputEventId: this.activeAudioInputEventId,
      chunks: [chunk],
    })
  }

  sendAudioStreamEnd(): void {
    const inputEventId = this.activeAudioInputEventId ?? createLiveInputEventId()
    if (this.sendRealtimeInput({ inputEventId, audioStreamEnd: true })) {
      this.activeAudioInputEventId = null
    }
  }

  sendVideoFrame(frame: LiveMediaFrame): boolean {
    return this.sendRealtimeInput({ video: frame })
  }

  sendContextUpdate(update: LiveContextUpdate): void {
    this.sendMessage({
      type: 'CONTEXT_UPDATE',
      payload: update,
    } as LiveGatewayClientMessage)
  }

  sendPolicyUpdate(policy: LivePolicyUpdate): void {
    this.sendMessage({
      type: 'policy_update',
      payload: { policy },
    } as LiveGatewayClientMessage)
  }

  sendToolResponse(response: unknown): void {
    const payload = normalizeLiveToolResponsePayload(response)
    if (!payload) {
      this.emit({
        type: 'error',
        message: 'Invalid tool response payload',
        code: 'INVALID_TOOL_RESPONSE',
      })
      return
    }

    this.sendMessage({ type: 'tool_response', payload })
  }

  getSessionResumptionHandle(): string | null {
    return this.resumptionHandle
  }

  getConnectionId(): string | null {
    return this.connectionId
  }

  getLiveStartAttemptId(): string | null {
    return this.liveStartAttemptId
  }

  getLastCloseInfo(): { code: number | null; reason: string | null } {
    return { code: this.previousCloseCode, reason: this.previousCloseReason }
  }

  getBufferedAmount(): number {
    return this.connection?.socket.bufferedAmount ?? 0
  }

  getIsSessionReady(): boolean {
    return this.state === 'connected'
  }

  resumeOpenTransport(): boolean {
    if (!this.connection?.isOpen()) return false
    if (this.state !== 'connected') this.setState('connected')
    return true
  }

  async resumeSession(handle: string): Promise<void> {
    if (!this.lastConnectArgs) {
      throw new Error('Cannot resume Live session before an initial connect')
    }

    await this.disconnect()
    this.liveStartAttemptId = createLiveStartAttemptId()
    this.setState('reconnecting')

    logger.info('[LiveSessionAdapter] Live start (resume)', {
      liveStartAttemptId: this.liveStartAttemptId,
      sessionId: this.lastConnectArgs.sessionId,
      isResume: true,
      isReconnect: this.hasConnectedBefore,
      previousCloseCode: this.previousCloseCode,
      previousCloseReason: this.previousCloseReason,
    })

    try {
      const connection = await createGatewayConnection()
      this.connection = connection
      this.bindConnection(connection)

      const sessionCapability = await issueLiveSessionCapability(
        this.lastConnectArgs.sessionId ?? '',
      )
      await this.startSession('session_resume', {
        ...this.buildSessionStartPayload(this.lastConnectArgs),
        sessionCapability,
        handle,
        sessionResumptionHandle: handle,
      })
      this.emit({ type: 'resumed' })
    } catch (error) {
      this.setState('error')
      this.emit({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to resume Live session',
      })
      await this.disconnect()
      throw error
    }
  }

  onEvent(cb: EventListener): () => void {
    this.listeners.add(cb)
    return () => this.listeners.delete(cb)
  }

  private async startSession(
    type: 'session_start' | 'session_resume',
    payload: LiveGatewaySessionStartPayload & { handle?: string }
  ): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.rejectPendingConnect(
          new Error(
            `Live session did not start within ${WEBSOCKET_CONFIG.HEARTBEAT_TIMEOUT_MS}ms`
          )
        )
      }, WEBSOCKET_CONFIG.HEARTBEAT_TIMEOUT_MS)

      this.pendingConnect = {
        resolve: () => {
          clearTimeout(timeoutId)
          resolve()
        },
        reject: error => {
          clearTimeout(timeoutId)
          reject(error)
        },
      }
      const sent = this.sendMessage({ type, payload } as LiveGatewayClientMessage)
      if (!sent) {
        this.rejectPendingConnect(new Error('Failed to send Live session start message'))
      }
    })
  }

  private bindConnection(connection: GatewayConnection): void {
    this.cleanupConnection()

    this.cleanupCallbacks.add(
      connection.onMessage(event => {
        const raw = typeof event.data === 'string' ? event.data : String(event.data)
        const message = safeParseJson<LiveGatewayServerMessage | null>(raw, null, {
          onError: error => {
            logger.warn('[LiveSessionAdapter] Failed to parse gateway message', {
              error: error instanceof Error ? error.message : String(error),
              preview: raw.slice(0, 160),
            })
          },
        })

        if (message) {
          this.routeGatewayMessage(message)
        }
      })
    )

    this.cleanupCallbacks.add(
      connection.onClose(event => {
        if (this.isDisconnecting) {
          logger.debug('[LiveSessionAdapter] onClose during disconnect — ignored')
          return
        }
        const reason = event.reason || 'socket_closed'
        this.previousCloseCode = typeof event.code === 'number' ? event.code : null
        this.previousCloseReason = reason
        logger.debug('[LiveSessionAdapter] gateway closed', { code: event.code, reason })
        this.rejectPendingConnect(new Error(`Live gateway closed: ${reason} (code: ${event.code})`))
        this.connection = null
        this.handleSessionClosed(this.previousCloseCode, reason)
      })
    )

    this.cleanupCallbacks.add(
      connection.onError(event => {
        logger.debug('[LiveSessionAdapter] gateway socket error', { type: event.type })
        this.rejectPendingConnect(new Error('Live gateway socket error'))
        this.setState('error')
        this.emit({ type: 'error', message: 'Live gateway socket error' })
      })
    )
  }

  private routeGatewayMessage(message: LiveGatewayServerMessage): void {
    switch (message.type) {
      case 'session_started':
        this.connectionId = message.payload.connectionId
        this.hasConnectedBefore = true
        this.previousCloseCode = null
        this.previousCloseReason = null
        logger.info('[LiveSessionAdapter] gateway session started', {
          liveStartAttemptId: this.liveStartAttemptId,
          connectionId: this.connectionId,
        })
        this.setState('connected')
        this.resolvePendingConnect()
        this.emit({ type: 'connected' })
        break

      case 'session_ready':
        this.emit({
          type: 'sessionReady',
          ...(message.payload?.sessionId ? { sessionId: message.payload.sessionId } : {}),
          ...(message.payload?.timestamp ? { timestamp: message.payload.timestamp } : {}),
        })
        break

      case 'setup_complete':
        this.emit({ type: 'setupComplete' })
        break

      case 'session_closed': {
        const code = typeof message.payload?.code === 'number' ? message.payload.code : null
        if (code !== null) this.previousCloseCode = code
        if (message.payload?.reason) this.previousCloseReason = message.payload.reason
        this.handleSessionClosed(code, message.payload?.reason)
        break
      }

      case 'transcript':
        logger.info('[LiveSessionAdapter] transcript', {
          kind: message.payload.kind,
          isFinal: message.payload.isFinal,
          textLen: typeof message.payload.text === 'string' ? message.payload.text.length : 0,
          textPreview:
            typeof message.payload.text === 'string'
              ? message.payload.text.slice(0, 80)
              : undefined,
          hasTurnId: Boolean(message.payload.turnId),
        })
        if (message.payload.kind === 'input') {
          this.emit({
            type: 'inputTranscript',
            text: message.payload.text,
            isFinal: message.payload.isFinal,
            ...(message.payload.turnId ? { turnId: message.payload.turnId } : {}),
            ...(message.payload.source ? { source: message.payload.source } : {}),
          })
        } else {
          this.emit({
            type: 'outputTranscript',
            text: message.payload.text,
            isFinal: message.payload.isFinal,
            ...(message.payload.groundingMetadata
              ? { groundingMetadata: message.payload.groundingMetadata }
              : {}),
            ...(message.payload.turnId ? { turnId: message.payload.turnId } : {}),
            ...(message.payload.source ? { source: message.payload.source } : {}),
          })
        }
        break

      case 'text':
        this.emit({
          type: 'text',
          text: message.payload.text,
          ...(message.payload.turnId ? { turnId: message.payload.turnId } : {}),
          ...(message.payload.source ? { source: message.payload.source } : {}),
        })
        break

      case 'audio_output':
        this.emit({
          type: 'audioOutput',
          pcmBase64: message.payload.pcmBase64,
          ...(message.payload.mimeType ? { mimeType: message.payload.mimeType } : {}),
        })
        break

      case 'tool_call':
        this.emit({
          type: 'toolCall',
          calls: normalizeLiveToolCalls(
            message.payload.calls.length > 0 ? message.payload.calls : message.payload.raw
          ),
        })
        break

      case 'interrupted':
        this.emit({ type: 'interrupted' })
        break

      case 'turn_complete':
        this.activeAudioInputEventId = null
        this.emit({
          type: 'turnComplete',
          ...(message.payload?.turnId ? { turnId: message.payload.turnId } : {}),
          ...(message.payload?.source ? { source: message.payload.source } : {}),
        })
        break

      case 'generation_complete':
        this.activeAudioInputEventId = null
        this.emit({ type: 'generationComplete' })
        break

      case 'go_away':
        this.emit({ type: 'goAway', timeLeft: message.payload?.timeLeft })
        break

      case 'session_resumption_update':
        this.resumptionHandle = message.payload.handle
        this.emit({
          type: 'sessionResumptionUpdate',
          handle: message.payload.handle,
          ...(message.payload.resumable !== undefined
            ? { resumable: message.payload.resumable }
            : {}),
        })
        break

      case 'response_mode_changed':
      case 'language_changed':
        logger.debug('[LiveSessionAdapter] Gateway policy event received', {
          type: message.type,
          payload: message.payload,
        })
        break

      case 'heartbeat':
        this.sendMessage({ type: 'heartbeat_ack' } as LiveGatewayClientMessage)
        break

      case 'input_stored':
        if (
          message.payload.inputEventId &&
          message.payload.inputEventId === this.typedLinePayload?.inputEventId
        ) {
          this.typedLinePayload = null
          this.typedLineResendsLeft = 0
        }
        break

      case 'input_queued':
        break

      case 'error':
        if (message.payload.code === LIVE_LINE_NOT_STORED) {
          // The socket is still up. Resend the same typed input. A second miss
          // stays on screen and becomes a visible error. Do not mark the session dead.
          if (!this.typedLinePayload) break
          const resent = this.resendUnstoredTypedLine()
          this.emit({
            type: 'error',
            message: message.payload.message,
            code: resent ? LIVE_LINE_NOT_STORED : LIVE_LINE_UNCONFIRMED,
          })
          break
        }
        this.rejectPendingConnect(new Error(message.payload.message))
        this.setState('error')
        this.emit({
          type: 'error',
          message: message.payload.message,
          ...(message.payload.code ? { code: message.payload.code } : {}),
        })
        break

      case 'signal': {
        const { action, toolName, turnId, data } = message.payload
        if (action === 'tool_executing' && toolName) {
          this.emit({ type: 'toolStatus', status: 'executing', toolName })
        } else if (action === 'tool_complete' && toolName) {
          this.emit({ type: 'toolStatus', status: 'complete', toolName })
        } else if (action === 'trigger_report') {
          this.emit({
            type: 'signal',
            action: 'trigger_report',
            ...(turnId ? { turnId } : {}),
            ...(data ? { data } : {}),
          })
        } else if (action === 'trigger_booking') {
          this.emit({
            type: 'signal',
            action: 'trigger_booking',
            ...(turnId ? { turnId } : {}),
            ...(data ? { data } : {}),
          })
        }
        break
      }

      case 'stage_update':
        this.emit({
          type: 'stageUpdate',
          ...(message.payload.turnId ? { turnId: message.payload.turnId } : {}),
          ...(message.payload.stage ? { stage: message.payload.stage } : {}),
          ...(message.payload.agent ? { agent: message.payload.agent } : {}),
          ...(message.payload.metadata ? { metadata: message.payload.metadata } : {}),
        })
        break

      case 'session_projection':
        this.emit({ type: 'sessionProjection', envelope: message.payload })
        break

      default:
        logger.warn('[LiveSessionAdapter] Unhandled gateway message', {
          type: (message as { type?: string }).type,
        })
    }
  }

  /**
   * Single close path for both transport close and gateway `session_closed`.
   * A Gemini 1007 (INVALID_FRAME_PAYLOAD_DATA) poisons the session: we surface it
   * as an error (releasing Live ownership upstream) and force the next start to be a
   * fresh connect. We never retry/resume into a 1007-closed session.
   */
  private handleSessionClosed(code: number | null, reason?: string): void {
    const safeReason = reason || 'socket_closed'
    if (code === LIVE_INVALID_FRAME_CLOSE_CODE) {
      logger.warn('[LiveSessionAdapter] Live closed with 1007 — poisoned session, requiring fresh start', {
        liveStartAttemptId: this.liveStartAttemptId,
        connectionId: this.connectionId,
        previousCloseCode: this.previousCloseCode,
        previousCloseReason: this.previousCloseReason,
      })
      // Drop any resume affordance; the next connect() must be fully fresh.
      this.resumptionHandle = null
      this.setState('error')
      this.emit({
        type: 'error',
        message: 'Live session closed unexpectedly (invalid frame). Tap the microphone to start a new session.',
        code: 'LIVE_INVALID_FRAME',
      })
      return
    }

    if (this.state !== 'error') {
      this.setState('disconnected')
    }
    this.emit({ type: 'disconnected', reason: safeReason })
  }

  private buildSessionStartPayload(args: LiveConnectArgs): LiveGatewaySessionStartPayload {
    const payload: LiveGatewaySessionStartPayload = {}

    if (args.sessionId) payload.sessionId = args.sessionId
    if (this.liveStartAttemptId) payload.liveStartAttemptId = this.liveStartAttemptId
    if (args.languageCode) payload.languageCode = args.languageCode
    if (args.voiceName) payload.voiceName = args.voiceName
    if (args.responseMode) payload.responseMode = args.responseMode
    if (args.affectiveState) payload.affectiveState = args.affectiveState
    if (args.visualAttentionEnabled !== undefined) {
      payload.visualAttentionEnabled = args.visualAttentionEnabled
    }
    if (args.proactiveAudioEnabled !== undefined) {
      payload.proactiveAudioEnabled = args.proactiveAudioEnabled
    }
    if (args.visualStreamActive !== undefined) {
      payload.visualStreamActive = args.visualStreamActive
    }
    return payload
  }

  private sendRealtimeInput(payload: LiveGatewayRealtimeInputPayload): boolean {
    const typedLine = Boolean(payload.text?.trim()) && payload.turnComplete === true
    if (typedLine) {
      this.typedLinePayload = payload
      this.typedLineResendsLeft = LIVE_TYPED_LINE_CLIENT_RESENDS
    }
    const sent = this.sendMessage({ type: 'realtime_input', payload })
    if (!sent && typedLine && !payload.text!.trim().startsWith('[SAY]')) {
      // The line is on screen but never left the browser (E2E 318 run B). Say so, same as a
      // line the server could not store, so the visitor sends it again.
      this.typedLinePayload = null
      this.typedLineResendsLeft = 0
      this.emit({
        type: 'error',
        message: 'Typed line was not sent: the Live connection is closed',
        code: LIVE_LINE_UNCONFIRMED,
      })
    }
    return sent
  }

  private resendUnstoredTypedLine(): boolean {
    const payload = this.typedLinePayload
    if (!payload || this.typedLineResendsLeft <= 0) return false
    this.typedLineResendsLeft -= 1
    return this.sendMessage({ type: 'realtime_input', payload })
  }

  private sendMessage(message: LiveGatewayClientMessage): boolean {
    if (!this.connection?.isOpen()) {
      this.emit({
        type: 'error',
        message: 'Live gateway connection is not open',
        code: 'LIVE_GATEWAY_NOT_OPEN',
      })
      return false
    }

    return this.connection.send(message)
  }

  private setState(state: LiveSessionState): void {
    if (this.state !== state) {
      logger.debug('[LiveSessionAdapter] adapter state change', { from: this.state, to: state })
    }
    this.state = state
  }

  private emit(event: LiveSessionEvent): void {
    for (const listener of this.listeners) {
      try {
        listener(event)
      } catch (error) {
        logger.error(
          '[LiveSessionAdapter] Event listener failed',
          error instanceof Error ? error : new Error(String(error)),
          { eventType: event.type }
        )
      }
    }
  }

  private resolvePendingConnect(): void {
    const pending = this.pendingConnect
    this.pendingConnect = null
    pending?.resolve()
  }

  private rejectPendingConnect(error: Error): void {
    const pending = this.pendingConnect
    this.pendingConnect = null
    pending?.reject(error)
  }

  private cleanupConnection(): void {
    for (const cleanup of this.cleanupCallbacks) {
      cleanup()
    }
    this.cleanupCallbacks.clear()
  }
}
